package org.zq.astrock_ai.ui.components

import org.zq.astrock_ai.theme.LocalAppFontFamily

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import org.zq.astrock_ai.data.ConfidenceItem
import org.zq.astrock_ai.data.DepthLevel
import org.zq.astrock_ai.data.FactorTag
import org.zq.astrock_ai.data.FactorType
import org.zq.astrock_ai.data.MetricColor
import org.zq.astrock_ai.data.MetricItem
import org.zq.astrock_ai.data.MockStockData
import org.zq.astrock_ai.data.NewsItem
import org.zq.astrock_ai.data.NewsTagType
import org.zq.astrock_ai.data.RelatedStock
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import org.zq.astrock_ai.data.StockProfile
import org.zq.astrock_ai.data.WatchlistItem
import androidx.compose.ui.text.style.TextAlign
import org.zq.astrock_ai.theme.BinanceColors

@Composable
fun WatchlistPage(
    items: List<WatchlistItem>,
    selectedIndex: Int,
    onSelect: (Int) -> Unit,
    onAddClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    GlassPanel(modifier = modifier.fillMaxWidth()) {
        Column {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 16.dp, vertical = 14.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(
                    "我的自选股",
                    fontWeight = FontWeight.SemiBold,
                    fontSize = 16.sp,
                    color = BinanceColors.Body,
                )
                Text(
                    "+ 添加",
                    fontSize = 14.sp,
                    color = BinanceColors.Yellow,
                    fontWeight = FontWeight.Medium,
                    modifier = Modifier
                        .clip(RoundedCornerShape(6.dp))
                        .clickable(onClick = onAddClick)
                        .padding(horizontal = 8.dp, vertical = 4.dp),
                )
            }
            HorizontalDivider(color = BinanceColors.Hairline)
            items.forEachIndexed { index, item ->
                WatchlistRow(
                    item = item,
                    active = index == selectedIndex,
                    onClick = { onSelect(index) },
                )
                if (index < items.lastIndex) {
                    HorizontalDivider(color = BinanceColors.Hairline)
                }
            }
        }
    }
}

@Composable
fun WatchlistSidebar(
    items: List<WatchlistItem> = MockStockData.watchlist,
    selectedIndex: Int,
    onSelect: (Int) -> Unit,
    onAddClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    GlassPanel(modifier = modifier.width(256.dp)) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 12.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text("自选股", fontWeight = FontWeight.SemiBold, fontSize = 14.sp, color = BinanceColors.Body)
            Text(
                "+",
                color = BinanceColors.Yellow,
                fontWeight = FontWeight.Bold,
                fontSize = 18.sp,
                modifier = Modifier
                    .clip(RoundedCornerShape(6.dp))
                    .clickable(onClick = onAddClick)
                    .padding(horizontal = 6.dp, vertical = 2.dp),
            )
        }
        HorizontalDivider(color = BinanceColors.Hairline)
        items.forEachIndexed { index, item ->
            WatchlistRow(
                item = item,
                active = index == selectedIndex,
                onClick = { onSelect(index) },
            )
            HorizontalDivider(color = BinanceColors.Hairline)
        }
    }
}

@Composable
fun AddWatchlistDialog(
    availableStocks: List<StockProfile>,
    onDismiss: () -> Unit,
    onAdd: (String) -> Unit,
) {
    var searchQuery by remember { mutableStateOf("") }
    val filtered = remember(availableStocks, searchQuery) {
        val query = searchQuery.trim()
        if (query.isEmpty()) {
            availableStocks
        } else {
            availableStocks.filter {
                it.name.contains(query, ignoreCase = true) || it.code.contains(query)
            }
        }
    }

    Dialog(onDismissRequest = onDismiss) {
        GlassPanel(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp),
        ) {
            Column(Modifier.padding(20.dp)) {
                Text("添加自选股", fontWeight = FontWeight.SemiBold, fontSize = 17.sp, color = BinanceColors.Body)
                Spacer(Modifier.height(16.dp))
                OutlinedTextField(
                    value = searchQuery,
                    onValueChange = { searchQuery = it },
                    modifier = Modifier.fillMaxWidth(),
                    placeholder = { Text("搜索股票名称或代码", color = BinanceColors.Muted, fontSize = 14.sp) },
                    singleLine = true,
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedTextColor = BinanceColors.Body,
                        unfocusedTextColor = BinanceColors.Body,
                        focusedBorderColor = BinanceColors.Yellow,
                        unfocusedBorderColor = BinanceColors.Hairline,
                        cursorColor = BinanceColors.Yellow,
                        focusedContainerColor = BinanceColors.Elevated,
                        unfocusedContainerColor = BinanceColors.Elevated,
                    ),
                )
                Spacer(Modifier.height(12.dp))
                if (filtered.isEmpty()) {
                    Text(
                        if (availableStocks.isEmpty()) "自选列表已包含全部可选股票" else "未找到匹配股票",
                        fontSize = 14.sp,
                        color = BinanceColors.Muted,
                        modifier = Modifier.padding(vertical = 24.dp),
                        textAlign = TextAlign.Center,
                    )
                } else {
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(280.dp)
                            .verticalScroll(rememberScrollState()),
                    ) {
                        filtered.forEach { stock ->
                            AddWatchlistCandidateRow(
                                stock = stock,
                                onClick = {
                                    onAdd(stock.code)
                                    onDismiss()
                                },
                            )
                            HorizontalDivider(color = BinanceColors.Hairline.copy(alpha = 0.5f))
                        }
                    }
                }
                Spacer(Modifier.height(12.dp))
                Text(
                    "取消",
                    fontSize = 14.sp,
                    color = BinanceColors.Muted,
                    modifier = Modifier
                        .fillMaxWidth()
                        .clip(RoundedCornerShape(8.dp))
                        .clickable(onClick = onDismiss)
                        .padding(vertical = 10.dp),
                    textAlign = TextAlign.Center,
                )
            }
        }
    }
}

@Composable
private fun AddWatchlistCandidateRow(stock: StockProfile, onClick: () -> Unit) {
    val changeColor = if (stock.isUp) BinanceColors.Green else BinanceColors.Red
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .padding(vertical = 12.dp, horizontal = 4.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Box(
                modifier = Modifier
                    .size(36.dp)
                    .clip(RoundedCornerShape(8.dp))
                    .background(Color(stock.avatarColor)),
                contentAlignment = Alignment.Center,
            ) {
                Text(stock.initial, color = Color.White, fontWeight = FontWeight.Bold, fontSize = 14.sp)
            }
            Spacer(Modifier.width(10.dp))
            Column {
                Text(stock.name, fontWeight = FontWeight.Medium, fontSize = 15.sp)
                Text(stock.fullCode, fontSize = 12.sp, color = BinanceColors.Muted)
            }
        }
        Column(horizontalAlignment = Alignment.End) {
            Text(stock.price, fontFamily = LocalAppFontFamily.current, fontSize = 14.sp, fontWeight = FontWeight.Medium)
            Text(stock.changePercent, fontFamily = LocalAppFontFamily.current, fontSize = 12.sp, color = changeColor)
        }
    }
}

@Composable
private fun WatchlistRow(
    item: WatchlistItem,
    active: Boolean,
    onClick: () -> Unit,
) {
    val changeColor = if (item.isUp) BinanceColors.Green else BinanceColors.Red
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(if (active) BinanceColors.Yellow.copy(0.08f) else Color.Transparent)
            .border(
                width = if (active) 3.dp else 0.dp,
                color = if (active) BinanceColors.Yellow else Color.Transparent,
            )
            .clickable(onClick = onClick)
            .padding(horizontal = 16.dp, vertical = 14.dp),
    ) {
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
            Column {
                Text(item.name, fontWeight = FontWeight.SemiBold, fontSize = 15.sp)
                Text(item.code, fontSize = 12.sp, color = BinanceColors.Muted)
            }
            Column(horizontalAlignment = Alignment.End) {
                Text(item.price, fontFamily = LocalAppFontFamily.current, fontSize = 15.sp, fontWeight = FontWeight.Medium)
                Text(item.changePercent, fontFamily = LocalAppFontFamily.current, fontSize = 13.sp, color = changeColor)
            }
        }
    }
}

@Composable
fun StockSelectorBar(
    items: List<WatchlistItem> = MockStockData.watchlist,
    selectedIndex: Int,
    onSelect: (Int) -> Unit,
    modifier: Modifier = Modifier,
) {
    val scrollState = rememberScrollState()
    LaunchedEffect(selectedIndex) {
        scrollState.animateScrollTo((selectedIndex * 80).coerceAtLeast(0))
    }
    Row(
        modifier = modifier
            .fillMaxWidth()
            .horizontalScroll(scrollState),
        horizontalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        items.forEachIndexed { index, item ->
            val active = index == selectedIndex
            Text(
                item.name,
                fontSize = 13.sp,
                fontWeight = if (active) FontWeight.SemiBold else FontWeight.Normal,
                color = if (active) BinanceColors.Yellow else BinanceColors.Body,
                maxLines = 1,
                softWrap = false,
                modifier = Modifier
                    .clip(RoundedCornerShape(8.dp))
                    .background(if (active) BinanceColors.Yellow.copy(0.12f) else BinanceColors.Elevated)
                    .border(
                        1.dp,
                        if (active) BinanceColors.Yellow.copy(0.4f) else BinanceColors.Hairline,
                        RoundedCornerShape(8.dp),
                    )
                    .clickable { onSelect(index) }
                    .padding(horizontal = 14.dp, vertical = 8.dp),
            )
        }
    }
}

@Composable
fun MetricCards(metrics: List<MetricItem>, compact: Boolean = false) {
    if (compact) {
        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            metrics.chunked(2).forEach { row ->
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    row.forEach { metric ->
                        MetricCardItem(metric, Modifier.weight(1f), compact = true)
                    }
                    if (row.size == 1) Spacer(Modifier.weight(1f))
                }
            }
        }
    } else {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            metrics.forEach { metric ->
                MetricCardItem(metric, Modifier.weight(1f), compact = false)
            }
        }
    }
}

@Composable
private fun MetricCardItem(metric: MetricItem, modifier: Modifier, compact: Boolean) {
    GlassPanel(modifier = modifier) {
        Column(Modifier.padding(if (compact) 12.dp else 16.dp)) {
            Text(metric.label, fontSize = if (compact) 12.sp else 13.sp, color = BinanceColors.Muted)
            Spacer(Modifier.height(if (compact) 4.dp else 6.dp))
            Text(
                metric.value,
                fontFamily = LocalAppFontFamily.current,
                fontSize = if (compact) 15.sp else 17.sp,
                fontWeight = FontWeight.SemiBold,
                color = when (metric.valueColor) {
                    MetricColor.Green -> BinanceColors.Green
                    MetricColor.Red -> BinanceColors.Red
                    MetricColor.Yellow -> BinanceColors.Yellow
                    MetricColor.Body -> BinanceColors.Body
                },
            )
            Spacer(Modifier.height(4.dp))
            Text(metric.subLabel, fontSize = if (compact) 11.sp else 13.sp, color = BinanceColors.Muted)
        }
    }
}

@Composable
fun StockHeader(stock: StockProfile, compact: Boolean = false) {
    val changeColor = if (stock.isUp) BinanceColors.Green else BinanceColors.Red
    val displayPrice = stock.tradePrice

    GlassPanel(modifier = Modifier.fillMaxWidth()) {
        if (compact) {
            Column(Modifier.padding(14.dp)) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Box(
                        modifier = Modifier
                            .size(40.dp)
                            .clip(RoundedCornerShape(10.dp))
                            .background(Color(stock.avatarColor)),
                        contentAlignment = Alignment.Center,
                    ) {
                        Text(stock.initial, color = Color.White, fontWeight = FontWeight.Bold, fontSize = 16.sp)
                    }
                    Spacer(Modifier.width(10.dp))
                    Column(Modifier.weight(1f)) {
                        Text(stock.name, fontWeight = FontWeight.Bold, fontSize = 18.sp)
                        Text(stock.fullCode, fontSize = 12.sp, color = BinanceColors.Muted)
                    }
                }
                Spacer(Modifier.height(10.dp))
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.Bottom,
                ) {
                    Column {
                        Text(displayPrice, fontFamily = LocalAppFontFamily.current, fontSize = 24.sp, fontWeight = FontWeight.Bold, color = changeColor)
                        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                            Text(stock.changeAmount, fontFamily = LocalAppFontFamily.current, fontSize = 14.sp, color = changeColor)
                            Text(stock.changePercent, fontFamily = LocalAppFontFamily.current, fontSize = 14.sp, color = changeColor)
                        }
                    }
                    Column(horizontalAlignment = Alignment.End) {
                        Text(stock.industry, fontSize = 12.sp, color = BinanceColors.Muted)
                        Text("市值 ${stock.marketCap}", fontSize = 12.sp, color = BinanceColors.Muted)
                    }
                }
            }
        } else {
            Row(
                modifier = Modifier.padding(20.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
            Box(
                modifier = Modifier
                    .size(48.dp)
                    .clip(RoundedCornerShape(12.dp))
                    .background(Color(stock.avatarColor)),
                contentAlignment = Alignment.Center,
            ) {
                Text(stock.initial, color = Color.White, fontWeight = FontWeight.Bold, fontSize = 18.sp)
            }
            Spacer(Modifier.width(12.dp))
            Column(Modifier.weight(1f)) {
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    Text(stock.name, fontWeight = FontWeight.Bold, fontSize = 20.sp)
                    Text(
                        stock.fullCode,
                        fontSize = 13.sp,
                        color = BinanceColors.Muted,
                        modifier = Modifier
                            .clip(RoundedCornerShape(4.dp))
                            .background(BinanceColors.Elevated)
                            .padding(horizontal = 8.dp, vertical = 2.dp),
                    )
                    Text(
                        "融资融券",
                        fontSize = 13.sp,
                        color = BinanceColors.Green,
                        modifier = Modifier
                            .clip(RoundedCornerShape(4.dp))
                            .background(BinanceColors.Green.copy(0.1f))
                            .border(1.dp, BinanceColors.Green.copy(0.2f), RoundedCornerShape(4.dp))
                            .padding(horizontal = 8.dp, vertical = 2.dp),
                    )
                }
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    Text(stock.industry, fontSize = 13.sp, color = BinanceColors.Muted)
                    Text("·", fontSize = 13.sp, color = BinanceColors.Muted)
                    Text("市值 ${stock.marketCap}", fontSize = 13.sp, color = BinanceColors.Muted)
                }
            }
            Column(horizontalAlignment = Alignment.End) {
                Row(verticalAlignment = Alignment.Bottom, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    Text(displayPrice, fontFamily = LocalAppFontFamily.current, fontSize = 28.sp, fontWeight = FontWeight.Bold, color = changeColor)
                    Text(stock.changeAmount, fontFamily = LocalAppFontFamily.current, fontSize = 17.sp, color = changeColor)
                }
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    Text(stock.changePercent, fontFamily = LocalAppFontFamily.current, fontSize = 14.sp, color = changeColor)
                    Text("今开 ${stock.openPrice}", fontSize = 13.sp, color = BinanceColors.Muted)
                }
            }
            }
        }
    }
}

@Composable
fun AiPredictionPanel(stock: StockProfile, compact: Boolean = false) {
    val target = MockStockData.predictionTarget(stock)
    val stop = MockStockData.predictionStop(stock)
    val upside = MockStockData.predictionUpside(stock)
    val risk = MockStockData.predictionRisk(stock)
    val trendColor = if (stock.isUp) BinanceColors.Green else BinanceColors.Red
    val trendLabel = if (stock.isUp) "看涨" else "看跌"

    if (compact) {
        Column(modifier = Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(12.dp)) {
            AiPredictionMainCard(stock, target, stop, upside, risk, trendLabel, trendColor, compact = true)
            ConfidenceRingPanel(Modifier.fillMaxWidth())
        }
    } else {
        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(20.dp)) {
            AiPredictionMainCard(stock, target, stop, upside, risk, trendLabel, trendColor, compact = false, modifier = Modifier.weight(2f))
            ConfidenceRingPanel(Modifier.weight(1f))
        }
    }
}

@Composable
private fun AiPredictionMainCard(
    stock: StockProfile,
    target: String,
    stop: String,
    upside: String,
    risk: String,
    trendLabel: String,
    trendColor: Color,
    compact: Boolean,
    modifier: Modifier = Modifier,
) {
    GradientBorderPanel(modifier = modifier) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Box(Modifier.size(32.dp).clip(RoundedCornerShape(8.dp)).background(BinanceColors.Yellow.copy(0.1f)), contentAlignment = Alignment.Center) {
                    Text("⚡", fontSize = 14.sp)
                }
                Spacer(Modifier.width(8.dp))
                Text("${stock.name} · 预测", fontWeight = FontWeight.SemiBold, fontSize = if (compact) 16.sp else 17.sp)
                if (!compact) {
                    Spacer(Modifier.weight(1f))
                    Text("模型版本 v3.2.1", fontSize = 13.sp, color = BinanceColors.Muted,
                        modifier = Modifier.clip(RoundedCornerShape(4.dp)).background(BinanceColors.Elevated).padding(horizontal = 8.dp, vertical = 4.dp))
                }
            }
            if (compact) {
                Spacer(Modifier.height(6.dp))
                Text("模型版本 v3.2.1", fontSize = 12.sp, color = BinanceColors.Muted)
            }
            Spacer(Modifier.height(16.dp))
            if (compact) {
                Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    PredictionStatCard("预测趋势", trendLabel, "短期 (1-5日)", trendColor, isTrend = true, modifier = Modifier.fillMaxWidth())
                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                        PredictionStatCard("目标价位", target, upside, BinanceColors.Yellow, modifier = Modifier.weight(1f))
                        PredictionStatCard("止损建议", stop, risk, BinanceColors.Red, modifier = Modifier.weight(1f))
                    }
                }
            } else {
                Row(horizontalArrangement = Arrangement.spacedBy(16.dp), modifier = Modifier.fillMaxWidth()) {
                    PredictionStatCard("预测趋势", trendLabel, "短期 (1-5日)", trendColor, isTrend = true, modifier = Modifier.weight(1f))
                    PredictionStatCard("目标价位", target, upside, BinanceColors.Yellow, modifier = Modifier.weight(1f))
                    PredictionStatCard("止损建议", stop, risk, BinanceColors.Red, modifier = Modifier.weight(1f))
                }
            }
            Spacer(Modifier.height(20.dp))
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                Text("综合置信度", fontSize = 14.sp, color = BinanceColors.Muted)
                Text("78.5%", fontFamily = LocalAppFontFamily.current, fontSize = 17.sp, fontWeight = FontWeight.Bold, color = BinanceColors.Green)
            }
            Spacer(Modifier.height(8.dp))
            ConfidenceBar(0.785f)
            Spacer(Modifier.height(16.dp))
            Row(
                modifier = Modifier.horizontalScroll(rememberScrollState()),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                MockStockData.factorTags.forEach { FactorTagChip(it) }
            }
        }
}

@Composable
private fun PredictionStatCard(
    label: String,
    value: String,
    sub: String,
    color: Color,
    modifier: Modifier = Modifier,
    isTrend: Boolean = false,
) {
    Column(
        modifier = modifier
            .clip(RoundedCornerShape(12.dp))
            .background(BinanceColors.Dark.copy(0.5f))
            .border(1.dp, BinanceColors.Hairline.copy(0.5f), RoundedCornerShape(12.dp))
            .padding(16.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Text(label, fontSize = 13.sp, color = BinanceColors.Muted)
        Spacer(Modifier.height(8.dp))
        if (isTrend) {
            val arrow = if (value == "看跌") "↓" else "↑"
            Text("$arrow $value", fontSize = 22.sp, fontWeight = FontWeight.Bold, color = color)
        } else {
            Text(value, fontFamily = LocalAppFontFamily.current, fontSize = 22.sp, fontWeight = FontWeight.Bold, color = color)
        }
        Spacer(Modifier.height(6.dp))
        Text(sub, fontSize = 13.sp, color = color)
    }
}

@Composable
private fun ConfidenceBar(progress: Float) {
    BoxWithConstraints(
        modifier = Modifier
            .fillMaxWidth()
            .height(12.dp)
            .clip(RoundedCornerShape(6.dp))
            .background(
                Brush.horizontalGradient(listOf(BinanceColors.Red, BinanceColors.Yellow, BinanceColors.Green)),
            ),
    ) {
        Box(
            modifier = Modifier
                .align(Alignment.CenterStart)
                .padding(start = maxWidth * progress - 1.dp)
                .width(2.dp)
                .height(12.dp)
                .background(Color.White.copy(0.8f)),
        )
    }
    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
        Text("强烈看跌", fontSize = 12.sp, color = BinanceColors.Muted)
        Text("中性", fontSize = 12.sp, color = BinanceColors.Muted)
        Text("强烈看涨", fontSize = 12.sp, color = BinanceColors.Muted)
    }
}

@Composable
private fun FactorTagChip(tag: FactorTag) {
    val (bg, border, text) = when (tag.type) {
        FactorType.Positive -> Triple(BinanceColors.Green.copy(0.1f), BinanceColors.Green.copy(0.2f), BinanceColors.Green)
        FactorType.Warning -> Triple(BinanceColors.Yellow.copy(0.1f), BinanceColors.Yellow.copy(0.2f), BinanceColors.Yellow)
        FactorType.Negative -> Triple(BinanceColors.Red.copy(0.1f), BinanceColors.Red.copy(0.2f), BinanceColors.Red)
    }
    Text(
        tag.text,
        fontSize = 13.sp,
        color = text,
        modifier = Modifier
            .clip(RoundedCornerShape(8.dp))
            .background(bg)
            .border(1.dp, border, RoundedCornerShape(8.dp))
            .padding(horizontal = 12.dp, vertical = 6.dp),
    )
}

@Composable
private fun ConfidenceRingPanel(modifier: Modifier = Modifier) {
    GlassPanel(modifier = modifier) {
        Column(
            modifier = Modifier.padding(20.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Box(contentAlignment = Alignment.Center, modifier = Modifier.size(160.dp)) {
                Canvas(modifier = Modifier.fillMaxSize()) {
                    val stroke = 12f
                    val inset = stroke / 2
                    val arcSize = androidx.compose.ui.geometry.Size(size.width - stroke, size.height - stroke)
                    val topLeft = Offset(inset, inset)
                    drawArc(BinanceColors.Green, -90f, 252f, false, topLeft, arcSize, style = Stroke(stroke))
                    drawArc(BinanceColors.Elevated, 162f, 108f, false, topLeft, arcSize, style = Stroke(stroke))
                }
                Column(horizontalAlignment = Alignment.CenterHorizontally) {
                    Text("78.5", fontFamily = LocalAppFontFamily.current, fontSize = 28.sp, fontWeight = FontWeight.Bold, color = BinanceColors.Green)
                    Text("置信度 %", fontSize = 13.sp, color = BinanceColors.Muted)
                }
            }
            Spacer(Modifier.height(16.dp))
            MockStockData.confidenceItems.forEach { ConfidenceRow(it) }
        }
    }
}

@Composable
private fun ConfidenceRow(item: ConfidenceItem) {
    val barColor = when {
        item.percent >= 80 -> BinanceColors.Green
        item.percent >= 70 -> BinanceColors.Yellow
        else -> BinanceColors.Green
    }
    Row(
        modifier = Modifier.fillMaxWidth().padding(vertical = 6.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween,
    ) {
        Text(item.label, fontSize = 14.sp, color = BinanceColors.Muted, modifier = Modifier.width(48.dp))
        Box(
            modifier = Modifier.weight(1f).height(8.dp).clip(RoundedCornerShape(4.dp)).background(BinanceColors.Elevated),
        ) {
            Box(
                modifier = Modifier
                    .fillMaxWidth(item.percent / 100f)
                    .height(8.dp)
                    .clip(RoundedCornerShape(4.dp))
                    .background(barColor),
            )
        }
        Text("${item.percent}%", fontFamily = LocalAppFontFamily.current, fontSize = 14.sp, color = barColor, modifier = Modifier.width(40.dp).padding(start = 8.dp))
    }
}

@Composable
fun NewsPanel(stock: StockProfile, compact: Boolean = false) {
    val items = MockStockData.newsForStock(stock)
    GlassPanel(modifier = Modifier.fillMaxWidth()) {
        Column(Modifier.padding(if (compact) 14.dp else 20.dp)) {
            Text("📰 近期资讯与公告", fontWeight = FontWeight.SemiBold, fontSize = if (compact) 16.sp else 17.sp)
            Spacer(Modifier.height(12.dp))
            Row(
                modifier = Modifier.horizontalScroll(rememberScrollState()),
                horizontalArrangement = Arrangement.spacedBy(4.dp),
            ) {
                listOf("全部", "公司公告", "研报", "新闻").forEachIndexed { i, t ->
                    Text(
                        t,
                        fontSize = 13.sp,
                        color = if (i == 0) BinanceColors.Body else BinanceColors.Muted,
                        modifier = Modifier.clickable { }.padding(horizontal = 12.dp, vertical = 6.dp),
                    )
                }
            }
            Spacer(Modifier.height(12.dp))
            items.forEach { NewsRow(it, compact) }
            Spacer(Modifier.height(12.dp))
            Text(
                "加载更多资讯",
                fontSize = 14.sp,
                color = BinanceColors.Muted,
                modifier = Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(8.dp))
                    .border(1.dp, BinanceColors.Hairline, RoundedCornerShape(8.dp))
                    .clickable { }
                    .padding(vertical = 10.dp),
                textAlign = TextAlign.Center,
            )
        }
    }
}

@Composable
private fun NewsRow(item: NewsItem, compact: Boolean = false) {
    val (tagBg, tagColor) = when (item.tagType) {
        NewsTagType.Bullish -> BinanceColors.Green.copy(0.1f) to BinanceColors.Green
        NewsTagType.Neutral -> BinanceColors.Yellow.copy(0.1f) to BinanceColors.Yellow
        NewsTagType.Bearish -> BinanceColors.Red.copy(0.1f) to BinanceColors.Red
    }
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .clickable { }
            .padding(start = 12.dp, top = 16.dp, bottom = 16.dp)
            .border(width = 2.dp, color = Color.Transparent),
    ) {
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
            Column(Modifier.weight(1f)) {
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    Text(item.tag, fontSize = 12.sp, fontWeight = FontWeight.Medium, color = tagColor,
                        modifier = Modifier.clip(RoundedCornerShape(4.dp)).background(tagBg).border(1.dp, tagColor.copy(0.2f), RoundedCornerShape(4.dp)).padding(horizontal = 8.dp, vertical = 2.dp))
                    Text(item.category, fontSize = 13.sp, color = BinanceColors.Muted)
                }
                Spacer(Modifier.height(6.dp))
                Text(item.title, fontSize = 14.sp, fontWeight = FontWeight.Medium, lineHeight = 22.sp, color = BinanceColors.Body)
                Spacer(Modifier.height(6.dp))
                Text(item.summary, fontSize = 13.sp, color = BinanceColors.Muted, maxLines = if (compact) 2 else 1)
            }
            if (!compact) {
                Text(item.time, fontSize = 13.sp, color = BinanceColors.Muted, modifier = Modifier.padding(start = 16.dp))
            }
        }
        if (compact) {
            Text(item.time, fontSize = 12.sp, color = BinanceColors.Muted, modifier = Modifier.padding(top = 6.dp))
        }
        HorizontalDivider(color = BinanceColors.Hairline.copy(0.5f), modifier = Modifier.padding(top = 16.dp))
    }
}

@Composable
fun RightSidebar(
    stock: StockProfile,
    onStockSelect: (String) -> Unit,
    modifier: Modifier = Modifier,
    compact: Boolean = false,
) {
    Column(
        modifier = if (compact) modifier.fillMaxWidth() else modifier.width(320.dp),
        verticalArrangement = Arrangement.spacedBy(if (compact) 12.dp else 20.dp),
    ) {
        MarketDepthPanel(
            stock = stock,
            compact = compact,
            modifier = if (compact) Modifier.fillMaxWidth() else Modifier,
        )
        RelatedStocksPanel(onStockSelect = onStockSelect)
        AiAssistantPanel()
    }
}

@Composable
private fun MarketDepthPanel(stock: StockProfile, compact: Boolean = false, modifier: Modifier = Modifier) {
    if (compact) {
        CompactMarketDepth(stock, modifier)
    } else {
        FullMarketDepth(stock, modifier)
    }
}

@Composable
private fun FullMarketDepth(stock: StockProfile, modifier: Modifier = Modifier) {
    val midColor = if (stock.isUp) BinanceColors.Green else BinanceColors.Red
    GlassPanel(modifier = modifier) {
        Column(Modifier.padding(20.dp)) {
            Text("盘口深度", fontWeight = FontWeight.SemiBold, fontSize = 14.sp)
            Spacer(Modifier.height(12.dp))
            stock.askLevels.forEach { DepthRow(it) }
            HorizontalDivider(color = BinanceColors.Hairline, modifier = Modifier.padding(vertical = 8.dp))
            Text(
                stock.tradePrice,
                fontFamily = LocalAppFontFamily.current,
                fontSize = 17.sp,
                fontWeight = FontWeight.Bold,
                color = midColor,
                modifier = Modifier.fillMaxWidth(),
                textAlign = TextAlign.Center,
            )
            HorizontalDivider(color = BinanceColors.Hairline, modifier = Modifier.padding(vertical = 8.dp))
            stock.bidLevels.forEach { DepthRow(it) }
        }
    }
}

@Composable
private fun CompactMarketDepth(stock: StockProfile, modifier: Modifier = Modifier) {
    val midColor = if (stock.isUp) BinanceColors.Green else BinanceColors.Red
    val visibleLevels = 3
    val asks = stock.askLevels.takeLast(visibleLevels)
    val bids = stock.bidLevels.take(visibleLevels)

    GlassPanel(modifier = modifier) {
        Column(Modifier.padding(horizontal = 14.dp, vertical = 12.dp)) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text("盘口深度", fontWeight = FontWeight.SemiBold, fontSize = 14.sp)
                Text("五档 ▾", fontSize = 12.sp, color = BinanceColors.Muted)
            }
            Spacer(Modifier.height(8.dp))
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
            ) {
                Text("卖盘", fontSize = 11.sp, color = BinanceColors.Red, modifier = Modifier.weight(1f))
                Text("买盘", fontSize = 11.sp, color = BinanceColors.Green, modifier = Modifier.weight(1f), textAlign = TextAlign.End)
            }
            Spacer(Modifier.height(4.dp))
            asks.forEachIndexed { index, ask ->
                CompactDepthRow(
                    ask = ask,
                    bid = bids.getOrNull(index),
                )
            }
            Spacer(Modifier.height(6.dp))
            Text(
                stock.tradePrice,
                fontFamily = LocalAppFontFamily.current,
                fontSize = 16.sp,
                fontWeight = FontWeight.Bold,
                color = midColor,
                modifier = Modifier.fillMaxWidth(),
                textAlign = TextAlign.Center,
            )
        }
    }
}

@Composable
private fun CompactDepthRow(ask: DepthLevel, bid: DepthLevel?) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 3.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Row(
            modifier = Modifier.weight(1f),
            horizontalArrangement = Arrangement.spacedBy(6.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(ask.label, fontSize = 11.sp, color = BinanceColors.Muted, modifier = Modifier.width(24.dp))
            Text(ask.price, fontFamily = LocalAppFontFamily.current, fontSize = 12.sp, color = BinanceColors.Red, modifier = Modifier.weight(1f))
            Text(ask.volume, fontFamily = LocalAppFontFamily.current, fontSize = 11.sp, color = BinanceColors.Muted, modifier = Modifier.width(28.dp), textAlign = TextAlign.End)
        }
        Box(
            modifier = Modifier
                .padding(horizontal = 8.dp)
                .width(1.dp)
                .height(14.dp)
                .background(BinanceColors.Hairline),
        )
        if (bid != null) {
            Row(
                modifier = Modifier.weight(1f),
                horizontalArrangement = Arrangement.spacedBy(6.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(bid.volume, fontFamily = LocalAppFontFamily.current, fontSize = 11.sp, color = BinanceColors.Muted, modifier = Modifier.width(28.dp))
                Text(bid.price, fontFamily = LocalAppFontFamily.current, fontSize = 12.sp, color = BinanceColors.Green, modifier = Modifier.weight(1f), textAlign = TextAlign.End)
                Text(bid.label, fontSize = 11.sp, color = BinanceColors.Muted, modifier = Modifier.width(24.dp), textAlign = TextAlign.End)
            }
        }
    }
}

@Composable
private fun DepthRow(level: DepthLevel) {
    val color = if (level.isAsk) BinanceColors.Red else BinanceColors.Green
    Row(Modifier.fillMaxWidth().padding(vertical = 2.dp), verticalAlignment = Alignment.CenterVertically) {
        Text(level.label, fontSize = 13.sp, color = BinanceColors.Muted, modifier = Modifier.width(36.dp))
        Text(level.price, fontFamily = LocalAppFontFamily.current, fontSize = 13.sp, color = color, modifier = Modifier.weight(1f), textAlign = TextAlign.End)
        Text(level.volume, fontFamily = LocalAppFontFamily.current, fontSize = 13.sp, color = BinanceColors.Muted, modifier = Modifier.width(32.dp), textAlign = TextAlign.End)
        Box(Modifier.width(48.dp).height(16.dp).padding(start = 8.dp)) {
            Box(
                modifier = Modifier
                    .align(Alignment.CenterEnd)
                    .fillMaxWidth(level.barWidth)
                    .height(16.dp)
                    .clip(RoundedCornerShape(2.dp))
                    .background(color.copy(if (level.label.endsWith("1")) 0.3f else 0.2f)),
            )
        }
    }
}

@Composable
private fun RelatedStocksPanel(onStockSelect: (String) -> Unit) {
    GlassPanel {
        Column(Modifier.padding(20.dp)) {
            Text("相关个股", fontWeight = FontWeight.SemiBold, fontSize = 14.sp)
            Spacer(Modifier.height(12.dp))
            MockStockData.relatedStocks.forEach { stock ->
                RelatedStockRow(stock, onClick = { onStockSelect(stock.code) })
            }
        }
    }
}

@Composable
private fun RelatedStockRow(stock: RelatedStock, onClick: () -> Unit) {
    val changeColor = if (stock.isUp) BinanceColors.Green else BinanceColors.Red
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(8.dp))
            .clickable { onClick() }
            .padding(vertical = 8.dp, horizontal = 12.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Box(Modifier.size(32.dp).clip(RoundedCornerShape(8.dp)).background(Color(stock.avatarColor)), contentAlignment = Alignment.Center) {
                Text(stock.initial, color = Color.White, fontSize = 13.sp, fontWeight = FontWeight.Bold)
            }
            Spacer(Modifier.width(8.dp))
            Column {
                Text(stock.name, fontSize = 14.sp, fontWeight = FontWeight.Medium)
                Text(stock.code, fontSize = 12.sp, color = BinanceColors.Muted)
            }
        }
        Column(horizontalAlignment = Alignment.End) {
            Text(stock.price, fontFamily = LocalAppFontFamily.current, fontSize = 14.sp, color = changeColor)
            Text(stock.changePercent, fontFamily = LocalAppFontFamily.current, fontSize = 12.sp, color = changeColor)
        }
    }
}

@Composable
private fun AiAssistantPanel() {
    GradientBorderPanel {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Box(Modifier.size(32.dp).clip(RoundedCornerShape(8.dp)).background(BinanceColors.Yellow.copy(0.1f)), contentAlignment = Alignment.Center) {
                Text("💡", fontSize = 14.sp)
            }
            Spacer(Modifier.width(8.dp))
            Text("智启助手", fontWeight = FontWeight.SemiBold, fontSize = 14.sp)
        }
        Spacer(Modifier.height(12.dp))
        listOf("📊 分析当前技术面形态", "📈 生成未来一周走势预测", "⚠️ 评估当前持仓风险").forEach { prompt ->
            Text(
                prompt,
                fontSize = 13.sp,
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(vertical = 4.dp)
                    .clip(RoundedCornerShape(8.dp))
                    .background(BinanceColors.Elevated.copy(0.5f))
                    .clickable { }
                    .padding(horizontal = 12.dp, vertical = 10.dp),
            )
            Spacer(Modifier.height(4.dp))
        }
        Spacer(Modifier.height(8.dp))
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            Text(
                "输入您的问题...",
                fontSize = 13.sp,
                color = BinanceColors.Muted,
                modifier = Modifier
                    .weight(1f)
                    .clip(RoundedCornerShape(8.dp))
                    .background(BinanceColors.Elevated)
                    .border(1.dp, BinanceColors.Hairline, RoundedCornerShape(8.dp))
                    .padding(horizontal = 12.dp, vertical = 8.dp),
            )
            Box(
                modifier = Modifier
                    .clip(RoundedCornerShape(8.dp))
                    .background(BinanceColors.Yellow)
                    .clickable { }
                    .padding(horizontal = 12.dp, vertical = 8.dp),
                contentAlignment = Alignment.Center,
            ) {
                Text("➤", color = BinanceColors.DarkText, fontSize = 14.sp)
            }
        }
    }
}
